// Browser error reporting (Sentry).
//
// Initialized from js/global.ts at module evaluation — before domReady and before the module
// registry bootstraps — so a module that throws during its own init is still captured.
//
// Configuration comes from window.__ENV__.sentry (templates/partials/env.html), so one compiled
// bundle reports correctly from any portal. The release identity is injected at build time as
// __SENTRY_RELEASE__ by build.mjs, which files the uploaded source map under the same value.
//
// ─── Why captureMessage becomes a breadcrumb ──────────────────────────────────
// Ten pre-existing call sites across usage-bayou, usage-lmi-bayou, entry-il-found, self-serve and
// the inline script in templates/thank-you.html call `Sentry.captureMessage("API Response", …)`
// on EVERY api response, at level "info", passing the raw request and response bodies in `extra`.
// In an enrollment flow those bodies carry names, addresses, and utility account numbers.
//
// Bridging them straight through would export customer PII to a third party on every API call and
// bury real errors under info-level noise. So `window.Sentry.captureMessage` records a breadcrumb
// carrying only the safe `tags` (endpoint, method, status, ok) and drops `extra` entirely. The API
// context then enriches whatever error actually occurs instead of becoming an event of its own,
// which is what this instrumentation should have been. No call site needed changing.
//
// `beforeSend` strips `extra` from outgoing events as well, so the guarantee holds even if a future
// call site sends something it shouldn't. New code should import `captureError`/`addApiBreadcrumb`
// from this module rather than reaching for the `window.Sentry` global.

import {
  addBreadcrumb,
  getClient,
  init,
  // Sentry's own ErrorEvent type, not the DOM one — this is the shape beforeSend receives.
  type ErrorEvent as SentryErrorEvent,
  type SeverityLevel,
  captureException as sentryCaptureException,
} from "@sentry/browser"

/** Severity strings the SDK accepts; anything else from legacy callers falls back to "info". */
const SEVERITY_LEVELS: readonly string[] = ["fatal", "error", "warning", "log", "info", "debug"]

/** Guards against a second client when initObservability is called more than once. */
let initialized = false

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function toSeverityLevel(value: unknown): SeverityLevel {
  return typeof value === "string" && SEVERITY_LEVELS.includes(value) ? (value as SeverityLevel) : "info"
}

/**
 * Drop `extra` from every outgoing event. Nothing in this theme legitimately needs it yet, and it
 * is the field the legacy call sites use for request/response bodies — so removing it wholesale is
 * the enforceable version of "do not ship PII". Relax deliberately, per field, if that changes.
 */
function scrubEvent(event: SentryErrorEvent): SentryErrorEvent {
  if (event.extra) event.extra = undefined
  return event
}

/** Record API context for later errors. Safe fields only — never request or response bodies. */
export function addApiBreadcrumb(message: string, data?: Record<string, unknown>): void {
  addBreadcrumb({ category: "api", message, level: "info", data })
}

/** Report an error. Prefer this over the window.Sentry global in new code. */
export function captureError(error: unknown, tags?: Record<string, unknown>): void {
  sentryCaptureException(error, tags ? { tags: tags as Record<string, string> } : undefined)
}

/**
 * The window.Sentry shim the legacy call sites reach through. Permanent, not transitional:
 * the inline script in templates/thank-you.html cannot import a module.
 */
const bridge: SentryBridge = {
  captureMessage(message, options) {
    const tags = isRecord(options?.tags) ? options.tags : undefined
    addBreadcrumb({
      category: "api",
      message,
      level: toSeverityLevel(options?.level),
      data: tags,
    })
  },
  captureException(error, options) {
    // Only tags travel with a legacy exception report; `extra` is dropped for the reason above.
    const tags = isRecord(options?.tags) ? (options.tags as Record<string, string>) : undefined
    sentryCaptureException(error, tags ? { tags } : undefined)
  },
}

/**
 * Initialize error reporting. A no-op when no DSN is configured, which keeps local builds, tests,
 * and any portal without a DSN completely unaffected rather than failing loudly.
 */
export function initObservability(): void {
  const config = window.__ENV__?.sentry
  if (!config?.dsn) return
  if (initialized || getClient()) return
  initialized = true

  init({
    dsn: config.dsn,
    // Derived from the portal's own env value so the two can never drift apart.
    environment: window.__ENV__.env,
    release: __SENTRY_RELEASE__,
    // Tracing and replay stay off until they are a deliberate decision with a sampling policy.
    tracesSampleRate: config.tracesSampleRate ?? 0,
    replaysSessionSampleRate: config.replaysSessionSampleRate ?? 0,
    // Never attach IP addresses, cookies, or request headers.
    sendDefaultPii: false,
    beforeSend: scrubEvent,
  })

  window.Sentry = bridge
}
